import { Dispatch, SetStateAction, useRef, useState } from 'react'
import type { DragEvent } from 'react'
import { mdiCloudUploadOutline } from '@mdi/js'
import { useIntl } from 'react-intl'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { homeConfigGenerateThenUploadTemplateZip } from './utils/generateThenUploadTemplateZip'
import { currentUserSelector } from 'features/auth/login/selectors'
import {
  DropzoneModalBody,
  DropzoneModalDesc,
  DropzoneModalIcon,
  DropzoneModalIconWrapper,
} from 'components/Modal/DropzoneModal/styles'
import { sleep } from 'app/utils/sleep'
import { selectedHomeSelector } from 'features/home/selectors'
import {
  HomeConfigModalProgressBar,
  HomeConfigModalProgressBarContainer,
} from '../../styles'
import { fetchHomeJobThunk } from 'features/home/components/HomeJobInfo/slice'

interface HomeConfigDropZoneProps {
  setUploadedFilename: (name: string) => void
  setIsUploadingEnd: Dispatch<SetStateAction<boolean>>
  setConfigId: Dispatch<SetStateAction<string>>
}

function HomeConfigDropZone({
  setUploadedFilename,
  setIsUploadingEnd,
  setConfigId,
}: HomeConfigDropZoneProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const [isDragActive, setIsDragActive] = useState(false)
  const [uploading, setUploading] = useState(0)
  const filename = useRef<string | null>(null)

  const home = useAppSelector(selectedHomeSelector.data)
  const user = useAppSelector(currentUserSelector)
  const userId = user?.id

  const handleFilesDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setErrorMessage(undefined)
    setIsLoading(true)
    setIsDragActive(false)
    try {
      // TODO: webkitGetAsEntry is an experimental technology, must be removed when new Savant upload service will be implemented
      const droppedFile = event?.dataTransfer?.items[0].webkitGetAsEntry()
      if (!droppedFile) return
      filename.current = droppedFile.name.trim()
      setUploadedFilename(droppedFile.name.trim())
      const configId = await homeConfigGenerateThenUploadTemplateZip(
        droppedFile,
        home?.domainId,
        userId,
        formatMessage,
        setUploading
      )
      // as unknown as string needs to say that its string
      // because initially configId is null
      setConfigId(configId as unknown as string)

      // backend issue - it needs ~4-5 sec to update templates
      await sleep(5000)
      if (home) {
        await dispatch(fetchHomeJobThunk(home.domainId, { configs: true }))
      }
      setIsLoading(false)
      setIsUploadingEnd(true)
    } catch (err) {
      setIsLoading(false)
      console.log('File upload failed', err)
      setErrorMessage((err as { message: string }).message)
    }
  }

  return (
    <DropzoneModalBody
      isDragActive={isDragActive}
      isDragReject={!!errorMessage}
      onDragOver={(event) => {
        event.preventDefault()
        setIsDragActive(true)
      }}
      onDragLeave={(event) => {
        event.preventDefault()
        setIsDragActive(false)
      }}
      onDrop={handleFilesDrop}
    >
      <DropzoneModalIconWrapper>
        <DropzoneModalIcon path={mdiCloudUploadOutline} />
      </DropzoneModalIconWrapper>
      <DropzoneModalDesc isDragReject={!!errorMessage}>
        {(errorMessage ||
          (filename.current && 'Uploading ' + filename.current)) ?? (
          <span>Drag and drop an .rpmConfig file to upload.</span>
        )}
      </DropzoneModalDesc>
      {isLoading && (
        <HomeConfigModalProgressBarContainer>
          <HomeConfigModalProgressBar progress={uploading} />
        </HomeConfigModalProgressBarContainer>
      )}
    </DropzoneModalBody>
  )
}

export default HomeConfigDropZone
